/**
 * printAssignedTask — Opens a new tab with a print-ready, brand-styled HTML
 * page for a single assigned task, then triggers window.print() so the user
 * can "Save as PDF" from the native browser dialog.
 *
 * Feb 2026 (iter-184) — Built after user's explicit request for a print button
 * on the task detail page. The design mirrors the CloudGM FZ-LLC brand
 * (blue → orange gradient GM icon + black text) and includes a fixed footer
 * with contact info on every page.
 *
 * Usage:
 *   import { printAssignedTask } from '../../utils/printAssignedTask';
 *   printAssignedTask(task, { language: 'ar' | 'en' });
 */

// iter-186 — LOGO_URL must be ABSOLUTE because we now serve the print HTML
// from a `blob:` URL (see the end of this file). A relative URL like
// '/pdf-logo-cloudgm.png' resolves against `blob://...` which yields a
// broken image; hence we prepend the current origin here.
const LOGO_URL = (typeof window !== 'undefined' ? window.location.origin : '') + '/pdf-logo-cloudgm.png';

const CONTACT = {
  sites:  ['cloudgm.com', 'cloudgm.business'],
  phone:  '+971 50 151 1917',
  emails: ['support@cloudgm.business', 'support@cloudgm.com'],
};

// Stage labels — mirror STAGE_CONFIG in AssignedTaskDetailPage.jsx.
const STAGES = [
  { key: 'pending_review',       ar: 'قيد المراجعة',        en: 'Pending review' },
  { key: 'approved',             ar: 'تمت الموافقة',       en: 'Approved' },
  { key: 'in_progress',          ar: 'قيد التنفيذ',        en: 'In progress' },
  { key: 'completed',            ar: 'تم الإنجاز',         en: 'Completed' },
  { key: 'commission_collected', ar: 'تحصيل العمولة',      en: 'Commission collected' },
  { key: 'commission_sent',      ar: 'تم إرسال العمولة',   en: 'Commission sent' },
  { key: 'closed',               ar: 'تم الانتهاء وإغلاق الطلب', en: 'Completed & Closed' },
];

const escapeHtml = (s) =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const fmtDate = (iso, isAr) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(isAr ? 'ar-EG' : 'en-GB'); }
  catch { return iso; }
};

const fmtAmount = (n) => {
  if (n == null || n === '') return '—';
  const v = Number(n);
  if (Number.isNaN(v)) return String(n);
  return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/**
 * Compute a human-readable status + current stage summary.
 * Returns { statusLabel, statusColor, currentStageLabel }.
 */
const computeStatus = (task, isAr) => {
  const rs   = task.request_stages || {};
  const done = (k) => rs[k]?.done === true;

  // Cancelled / Finalised / Ready-for-audit / Active with current stage.
  if (task.status === 'cancelled') {
    return {
      statusLabel: isAr ? 'ملغى' : 'Cancelled',
      statusColor: '#dc2626',
      currentStageLabel: '—',
    };
  }
  if (task.review_status === 'closed') {
    return {
      statusLabel: isAr ? 'مغلق ومدقق نهائياً' : 'Finalised',
      statusColor: '#059669',
      currentStageLabel: isAr ? 'تم الانتهاء وإغلاق الطلب' : 'Completed & Closed',
    };
  }
  if (task.review_status === 'ready_for_review') {
    return {
      statusLabel: isAr ? 'جاهز للتدقيق' : 'Ready for audit',
      statusColor: '#d97706',
      currentStageLabel: isAr ? 'تم الانتهاء وإغلاق الطلب' : 'Completed & Closed',
    };
  }

  // Find the LAST done stage that is not locked-only.
  let currentIdx = -1;
  for (let i = 0; i < STAGES.length; i += 1) {
    if (done(STAGES[i].key)) currentIdx = i;
  }
  const current = currentIdx >= 0 ? STAGES[currentIdx] : STAGES[0];
  return {
    statusLabel: isAr ? 'فعال' : 'Active',
    statusColor: '#0284c7',
    currentStageLabel: isAr ? current.ar : current.en,
  };
};

/**
 * Build the requested filename base (WITHOUT extension). Format matches the
 * user's exact request:  `بيانات_المقدم_[target_name]_طلب_[request_no]`.
 * — NO agent name.  — NO language suffix.  — Safe for all OSes.
 */
const buildFilenameBase = (task) => {
  const rawName = String(task.target_name || task.request_no_display || task.id || 'طلب').trim();
  const cleanName = rawName
    .replace(/[<>:"/\\|?*\u0000-\u001F]/g, '')   // Windows-forbidden chars
    .replace(/\s+/g, '_')
    .slice(0, 80) || 'مقدم';
  const reqNo = String(task.request_no_display || task.id || '').replace(/[^\u0600-\u06FFA-Za-z0-9-]/g, '');
  return `بيانات_المقدم_${cleanName}${reqNo ? '_طلب_' + reqNo : ''}`;
};

const buildHtml = (task, isAr, opts = {}) => {
  const dir = isAr ? 'rtl' : 'ltr';
  const t   = (ar, en) => (isAr ? ar : en);
  const downloadMode = !!opts.downloadMode;
  // Filename base drives the tab <title> which browsers use as the default
  // "Save as PDF" filename in the native print dialog.
  const filenameBase = buildFilenameBase(task);

  const status = computeStatus(task, isAr);

  const rs = task.request_stages || {};

  // Fees breakdown
  const govTotal = (task.actions || [])
    .filter((a) => a.has_fee)
    .reduce((s, a) => s + (Number(a.amount) || 0), 0);
  const serviceFee = Number(task.service_fee || 0);
  const isPlusGov  = task.fee_model === 'service_plus_gov';

  // iter-217c-v11 — dirham icon helper. Emits `<img>` inline with the number
  // so the browser's print engine embeds the PNG in the resulting PDF.
  const dh = (n) => `<span class="dh-price"><img src="${window.location.origin}/currency/dirham-black.png" alt="AED" class="dh-icon" />${fmtAmount(n)}</span>`;
  const totalDue   = task.total_price != null
    ? Number(task.total_price)
    : (isPlusGov ? govTotal + serviceFee : serviceFee);

  // ── Header ─────────────────────────────────────────────────
  const headerHtml = `
    <header class="doc-header">
      <div class="brand">
        <img src="${LOGO_URL}" alt="CloudGM FZ-LLC" class="brand-logo" />
      </div>
      <div class="doc-meta">
        <div class="doc-title">
          ${t('طلب خدمة / مهمة يدوية', 'Service Request / Manual Task')}
        </div>
        <div class="doc-ref">
          <span class="ref-label">${t('رقم الطلب', 'Request No.')}:</span>
          <span class="ref-value">${escapeHtml(task.request_no_display || task.id)}</span>
        </div>
        <div class="doc-date">
          <span class="ref-label">${t('تاريخ الإنشاء', 'Created')}:</span>
          <span>${fmtDate(task.created_at, isAr)}</span>
        </div>
      </div>
    </header>`;

  // ── Status hero ─────────────────────────────────────────────
  const statusHeroHtml = `
    <section class="status-hero">
      <div class="status-block" style="--pill:${status.statusColor}">
        <div class="status-label">${t('حالة الطلب', 'Request status')}</div>
        <div class="status-pill">${escapeHtml(status.statusLabel)}</div>
      </div>
      <div class="status-block current-stage">
        <div class="status-label">${t('المرحلة الحالية', 'Current stage')}</div>
        <div class="status-value">${escapeHtml(status.currentStageLabel)}</div>
      </div>
      ${task.priority ? `
      <div class="status-block">
        <div class="status-label">${t('الأولوية', 'Priority')}</div>
        <div class="status-value">${escapeHtml(task.priority)}</div>
      </div>` : ''}
    </section>`;

  // ── Target + Service ─────────────────────────────────────────
  const targetHtml = `
    <section class="section">
      <h2 class="section-title">${t('بيانات العميل / المستهدف', 'Client / Target')}</h2>
      <div class="grid-2">
        <div class="kv"><span class="k">${t('الاسم', 'Name')}</span><span class="v">${escapeHtml(task.target_name || '—')}</span></div>
        <div class="kv"><span class="k">${t('النوع', 'Type')}</span><span class="v">${task.target_type === 'agent' ? t('وكيل', 'Agent') : t('عميل', 'Customer')}</span></div>
        <div class="kv"><span class="k">${t('الهاتف', 'Phone')}</span><span class="v" dir="ltr">${escapeHtml(task.target_phone || '—')}</span></div>
        <div class="kv"><span class="k">${t('البريد الإلكتروني', 'Email')}</span><span class="v" dir="ltr">${escapeHtml(task.target_email || '—')}</span></div>
      </div>
    </section>

    <section class="section">
      <h2 class="section-title">${t('الخدمة المطلوبة', 'Requested service')}</h2>
      <div class="grid-2">
        <div class="kv"><span class="k">${t('الخدمة', 'Service')}</span><span class="v">${escapeHtml(isAr ? (task.service_name_ar || task.service_name_en) : (task.service_name_en || task.service_name_ar) || '—')}</span></div>
        ${(task.sub_service_name_ar || task.sub_service_name_en) ? `
        <div class="kv"><span class="k">${t('الخدمة الفرعية', 'Sub-service')}</span><span class="v">${escapeHtml(isAr ? (task.sub_service_name_ar || task.sub_service_name_en) : (task.sub_service_name_en || task.sub_service_name_ar))}</span></div>` : ''}
        <div class="kv"><span class="k">${t('مصدر الطلب', 'Source')}</span><span class="v">${escapeHtml(task.request_source || '—')}</span></div>
        <div class="kv"><span class="k">${t('منفذ المهمة', 'Executor')}</span><span class="v">${escapeHtml(task.assigned_to_name || '—')}</span></div>
        <div class="kv"><span class="k">${t('تاريخ التقديم', 'Submitted')}</span><span class="v">${escapeHtml(task.request_date || (task.created_at || '').slice(0, 10) || '—')}</span></div>
      </div>
      ${task.description_ar || task.description_en ? `
        <div class="note-box">
          <div class="note-label">${t('وصف الطلب', 'Description')}</div>
          <div>${escapeHtml(isAr ? (task.description_ar || task.description_en) : (task.description_en || task.description_ar))}</div>
        </div>` : ''}
      ${task.request_note ? `
        <div class="note-box internal">
          <div class="note-label">${t('ملاحظات داخلية', 'Internal note')}</div>
          <div>${escapeHtml(task.request_note)}</div>
        </div>` : ''}
    </section>`;

  // ── Fees ─────────────────────────────────────────────────────
  const feesHtml = `
    <section class="section">
      <h2 class="section-title">${t('الرسوم', 'Fees')}</h2>
      <div class="grid-2">
        <div class="kv"><span class="k">${t('نموذج الرسوم', 'Fee model')}</span><span class="v">${
          isPlusGov ? t('بكج كامل (خدمة + حكومية)', 'Full package (service + gov)')
                    : t('رسوم الخدمة فقط', 'Service fee only')
        }</span></div>
        <div class="kv"><span class="k">${t('رسم الخدمة', 'Service fee')}</span><span class="v">${dh(serviceFee)}</span></div>
        <div class="kv"><span class="k">${t('الرسوم الحكومية (من الإجراءات)', 'Government fees (from actions)')}</span><span class="v">${dh(govTotal)}</span></div>
        <div class="kv total"><span class="k">${t('المستحق على العميل', 'Total from customer')}</span><span class="v">${dh(totalDue)}</span></div>
      </div>
      ${task.commission_percent ? `
        <div class="grid-2" style="margin-top:8px">
          <div class="kv"><span class="k">${t('نسبة العمولة', 'Commission %')}</span><span class="v">${task.commission_percent}%</span></div>
          <div class="kv"><span class="k">${t('مبلغ العمولة', 'Commission amount')}</span><span class="v">${dh(task.commission_amount)}</span></div>
        </div>` : ''}
    </section>`;

  // ── Actions / procedures ─────────────────────────────────────
  // iter-218 (Feb 2026) — Attachments and receipts are now embedded
  // INSIDE each action's row (as inline image thumbnails) so the printed
  // PDF shows "Action 1 + its attachments", "Action 2 + its attachments",
  // etc — user's explicit request.
  const _apiBaseE = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_BACKEND_URL) || '';
  const _tokenE   = (typeof window !== 'undefined')
    ? (window.sessionStorage?.getItem('admin_token')
       || window.localStorage?.getItem('admin_token')
       || window.localStorage?.getItem('access_token')
       || window.localStorage?.getItem('token')
       || '')
    : '';
  const _fileIdUrlE = (id) => id
    ? `${_apiBaseE}/api/upload/file/${id}${_tokenE ? '?token=' + encodeURIComponent(_tokenE) : ''}`
    : null;
  const _resolveActionAtt = (att) => {
    if (att.data_url) return att.data_url;
    if (att.file_id)  return _fileIdUrlE(att.file_id);
    return null;
  };
  const _isImg = (m) => (m || '').toString().startsWith('image/');

  const actionsHtml = (task.actions || []).length ? `
    <section class="section">
      <h2 class="section-title">${t('الإجراءات المنجزة', 'Actions performed')} (${(task.actions || []).length})</h2>
      <div class="actions-list">
        ${task.actions.map((a, i) => {
          const recs = Array.isArray(a.receipts) && a.receipts.length
            ? a.receipts
            : (a.has_fee && a.receipt_file
                ? [{ name: a.receipt_name || 'receipt', amount: a.amount, file: a.receipt_file, mime: a.receipt_mime }]
                : []);
          // Image attachments for THIS action
          const actionImgs = (a.attachments || [])
            .map((att) => ({ ...att, _src: _resolveActionAtt(att) }))
            .filter((att) => att._src && _isImg(att.mime_type || att.mime));
          const actionDocs = (a.attachments || []).filter((att) => !_isImg(att.mime_type || att.mime));
          // Image receipts for THIS action
          const receiptImgs = recs
            .filter((r) => r.file && _isImg(r.mime))
            .map((r) => ({ src: `data:${r.mime};base64,${r.file}`, name: r.name || 'receipt', amount: r.amount }));
          return `
            <article class="action-card">
              <header class="action-head">
                <span class="action-num">${i + 1}</span>
                <div class="action-title">
                  ${escapeHtml(a.description || '—')}
                  ${a.has_fee ? `<span class="chip fee">${t('رسم حكومي', 'Gov fee')}</span>` : ''}
                </div>
                <div class="action-meta">
                  <span class="action-amt mono">${a.has_fee ? dh(a.amount) : '—'}</span>
                  <span class="action-date">${fmtDate(a.created_at, isAr)}</span>
                  <span class="action-by">${escapeHtml(a.created_by_name || a.created_by_email || '—')}</span>
                </div>
              </header>
              ${(actionImgs.length + receiptImgs.length) ? `
                <div class="action-imgs">
                  ${actionImgs.map((att, ai) => `
                    <figure class="action-img">
                      <img src="${att._src}" alt="${escapeHtml(att.label || att.name || '')}" loading="eager" crossorigin="anonymous" />
                      <figcaption>${t('مرفق', 'Attachment')} ${ai + 1}${att.label || att.name ? ` — ${escapeHtml(att.label || att.name)}` : ''}</figcaption>
                    </figure>
                  `).join('')}
                  ${receiptImgs.map((r, ri) => `
                    <figure class="action-img">
                      <img src="${r.src}" alt="${escapeHtml(r.name)}" loading="eager" crossorigin="anonymous" />
                      <figcaption>${t('إيصال', 'Receipt')} ${ri + 1}${r.amount != null ? ` — ${dh(r.amount)}` : ''}</figcaption>
                    </figure>
                  `).join('')}
                </div>` : ''}
              ${actionDocs.length ? `
                <div class="action-docs">
                  <strong>${t('مرفقات أخرى:', 'Other attachments:')}</strong>
                  ${actionDocs.map((att) => escapeHtml(att.label || att.name || '—')).join(' • ')}
                </div>` : ''}
            </article>`;
        }).join('')}
      </div>
    </section>` : '';

  // ── Attachments (original + from actions) — iter-217c-v11 shows ACTUAL image previews
  // iter-217c-v15 — also resolve `file_id` attachments (server-side files) via the
  //                 `/api/upload/file/{id}?token=…` URL so they render inside the PDF.
  const _apiBase = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_BACKEND_URL) || '';
  const _token   = (typeof window !== 'undefined')
    ? (window.sessionStorage?.getItem('admin_token')
       || window.localStorage?.getItem('admin_token')
       || window.localStorage?.getItem('access_token')
       || window.localStorage?.getItem('token')
       || '')
    : '';
  const _fileIdUrl = (id) => id
    ? `${_apiBase}/api/upload/file/${id}${_token ? '?token=' + encodeURIComponent(_token) : ''}`
    : null;
  const _resolveSrc = (a) => {
    if (a.data_url) return a.data_url;
    if (a.file_id)  return _fileIdUrl(a.file_id);
    return null;
  };

  const mergedAtts = [
    ...((task.attachments || []).map((a) => ({
      ...a,
      _source: 'original',
      _resolved_src: _resolveSrc(a),
    }))),
    ...((task.actions || []).flatMap((ac, ai) =>
      (ac.attachments || []).map((att) => ({
        ...att,
        _source: 'action',
        _actionIdx: ai + 1,
        _resolved_src: _resolveSrc(att),
      }))
    )),
  ];
  // Also include payment receipts and action receipts as image cards.
  const paymentReceipts = (task.payments || [])
    .filter((p) => p.receipt_file && (p.receipt_mime || '').startsWith('image/'))
    .map((p) => ({
      label: t('إيصال دفعة', 'Payment receipt'),
      name: p.receipt_name || `payment-${p.id}.png`,
      mime_type: p.receipt_mime,
      _resolved_src: `data:${p.receipt_mime};base64,${p.receipt_file}`,
      size: p.receipt_file ? (p.receipt_file.length * 0.75) : 0,
      _source: 'payment',
      _paymentId: p.id,
      _amount: p.amount,
    }));
  const actionReceipts = (task.actions || []).flatMap((ac, ai) =>
    (ac.receipts || [])
      .filter((r) => r.file && (r.mime || '').startsWith('image/'))
      .map((r) => ({
        label: t('إيصال إجراء', 'Action receipt'),
        name: r.name || `action-${ai + 1}-receipt.png`,
        mime_type: r.mime,
        _resolved_src: `data:${r.mime};base64,${r.file}`,
        size: r.file ? (r.file.length * 0.75) : 0,
        _source: 'action',
        _actionIdx: ai + 1,
      }))
  );
  const _isImageMime = (a) => (a.mime_type || a.mime || '').toString().startsWith('image/');
  const allImages = [...mergedAtts, ...paymentReceipts, ...actionReceipts]
    .filter((a) => a._resolved_src && _isImageMime(a));
  const nonImageAtts = [...mergedAtts, ...paymentReceipts, ...actionReceipts].filter(
    (a) => !a._resolved_src || !_isImageMime(a)
  );

  const _sourceChip = (a) => {
    if (a._source === 'original') return `<span class="chip src-original">${t('من الطلب الأصلي', 'Original')}</span>`;
    if (a._source === 'action')   return `<span class="chip src-action">${t(`إجراء #${a._actionIdx}`, `Action #${a._actionIdx}`)}</span>`;
    if (a._source === 'payment')  return `<span class="chip src-payment">${t(`دفعة ${a._amount || ''}`, `Payment ${a._amount || ''}`)}</span>`;
    return '';
  };

  const imagesGridHtml = allImages.length ? `
    <section class="section">
      <h2 class="section-title">${t('مرفقات وإيصالات', 'Attachments & receipts')} (${allImages.length})</h2>
      <div class="images-grid">
        ${allImages.map((a, i) => `
          <figure class="image-card">
            <img src="${a._resolved_src}" alt="${escapeHtml(a.label || a.name || '')}" loading="eager" crossorigin="anonymous" />
            <figcaption>
              <span class="mono">${i + 1}.</span>
              <span>${escapeHtml(a.label || a.name || '—')}</span>
              ${_sourceChip(a)}
            </figcaption>
          </figure>
        `).join('')}
      </div>
    </section>` : '';

  const attHtml = nonImageAtts.length ? `
    <section class="section">
      <h2 class="section-title">${t('مرفقات إضافية (بدون معاينة)', 'Other attachments (no preview)')} (${nonImageAtts.length})</h2>
      <ul class="attn-list">
        ${nonImageAtts.map((a, i) => `
          <li>
            <span class="mono">${i + 1}.</span>
            ${escapeHtml(a.label || a.name || '—')}
            ${_sourceChip(a)}
            <span class="attn-meta">${escapeHtml((a.mime_type || '').split('/')[1] || '')}${a.size ? ` • ${(a.size / 1024).toFixed(0)}KB` : ''}</span>
          </li>
        `).join('')}
      </ul>
    </section>` : '';

  // ── Stages timeline ──────────────────────────────────────────
  const stagesHtml = task.target_type === 'agent' ? `
    <section class="section">
      <h2 class="section-title">${t('مراحل الطلب', 'Request stages')}</h2>
      <ol class="stages">
        ${STAGES.map((s, idx) => {
          const info = rs[s.key] || {};
          const done = info.done === true || info.locked === true;
          return `
            <li class="${done ? 'done' : 'pending'}">
              <span class="stage-num">${idx + 1}</span>
              <span class="stage-label">${isAr ? s.ar : s.en}</span>
              <span class="stage-status">${done ? t('✓ تم', '✓ Done') : t('— لم يتم', '— Pending')}</span>
              <span class="stage-date">${info.at ? fmtDate(info.at, isAr) : ''}</span>
              ${info.by ? `<span class="stage-by">${escapeHtml(info.by)}</span>` : ''}
            </li>`;
        }).join('')}
      </ol>
    </section>` : '';

  // ── Cancelled reason (if applicable) ─────────────────────────
  const cancelHtml = task.status === 'cancelled' ? `
    <section class="section cancel-box">
      <h2 class="section-title">${t('سبب الإلغاء', 'Cancellation reason')}</h2>
      <div>${escapeHtml((isAr ? task.cancel_reason_ar : task.cancel_reason_en) || task.cancel_reason || '—')}</div>
      ${task.cancelled_at ? `<div class="attn-meta">${fmtDate(task.cancelled_at, isAr)}</div>` : ''}
    </section>` : '';

  // ── Footer (repeats via CSS @page) ───────────────────────────
  const footerHtml = `
    <div class="doc-footer">
      <div class="footer-bar"></div>
      <div class="footer-row">
        <span>🌐 ${CONTACT.sites.join(' • ')}</span>
        <span>📞 <span dir="ltr">${CONTACT.phone}</span></span>
        <span>✉ ${CONTACT.emails.join(' • ')}</span>
      </div>
      <div class="footer-line">CLOUD GM FZ-LLC — ${t('طُبع في', 'Printed on')} ${fmtDate(new Date().toISOString(), isAr)}</div>
    </div>`;

  return `<!doctype html>
<html lang="${isAr ? 'ar' : 'en'}" dir="${dir}">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(filenameBase)}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;800&family=Inter:wght@400;600;700&display=swap');

  /* Palette — sampled from the CloudGM FZ-LLC brand (blue → orange). */
  :root {
    --blue-500: #3B9EDC;
    --blue-700: #1c6ea4;
    --orange-500: #F5A55A;
    --orange-700: #d97706;
    --ink-900: #0f172a;
    --ink-600: #334155;
    --ink-400: #64748b;
    --ink-200: #e2e8f0;
    --paper:   #ffffff;
    --tint:    #f8fafc;
    --brand-grad: linear-gradient(90deg, var(--blue-500) 0%, var(--orange-500) 100%);
  }

  @page {
    size: A4;
    margin: 18mm 14mm 24mm 14mm;
  }
  html, body {
    margin: 0; padding: 0;
    background: var(--paper);
    color: var(--ink-900);
    font-family: ${isAr ? "'Cairo'" : "'Inter'"}, -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
    font-size: 12px;
    line-height: 1.55;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  /* ── Header ────────────────────────────────────────────────── */
  .doc-header {
    display: flex; align-items: center; gap: 20px;
    padding: 16px 18px;
    border: 1px solid var(--ink-200);
    border-radius: 14px;
    background: var(--tint);
    position: relative;
    overflow: hidden;
  }
  .doc-header::before {
    content: '';
    position: absolute; inset: 0 0 auto 0; height: 6px;
    background: var(--brand-grad);
  }
  .brand { flex-shrink: 0; }
  .brand-logo { height: 72px; width: auto; display: block; }
  .doc-meta { flex: 1; }
  .doc-title {
    font-size: 20px; font-weight: 800;
    color: var(--ink-900);
    letter-spacing: 0.2px;
    margin-bottom: 4px;
  }
  .doc-ref, .doc-date { font-size: 11px; color: var(--ink-600); }
  .doc-ref .ref-value { font-family: 'JetBrains Mono', 'Menlo', monospace; color: var(--blue-700); font-weight: 700; margin-inline-start: 6px; }
  .doc-date { margin-top: 2px; }
  .ref-label { color: var(--ink-400); font-weight: 600; }

  /* ── Status hero ───────────────────────────────────────────── */
  .status-hero {
    display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px;
    margin: 14px 0 6px 0;
  }
  .status-block {
    padding: 12px 14px;
    background: var(--tint);
    border: 1px solid var(--ink-200);
    border-radius: 10px;
    position: relative;
    overflow: hidden;
  }
  .status-block .status-label {
    font-size: 10px; text-transform: uppercase;
    letter-spacing: 0.6px; color: var(--ink-400); font-weight: 700;
    margin-bottom: 4px;
  }
  .status-value { font-size: 13px; font-weight: 700; color: var(--ink-900); }
  .status-pill {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 999px;
    background: var(--pill, var(--blue-500));
    color: #fff;
    font-weight: 800;
    font-size: 13px;
    letter-spacing: 0.3px;
  }
  .current-stage { border-color: var(--orange-500); background: linear-gradient(180deg, #fff 0%, #fff7ec 100%); }

  /* ── Section ───────────────────────────────────────────────── */
  .section {
    margin-top: 14px;
    page-break-inside: avoid;
  }
  .section-title {
    font-size: 12px;
    font-weight: 800;
    margin: 0 0 8px 0;
    padding: 6px 12px;
    background: var(--brand-grad);
    color: #fff;
    border-radius: 8px;
    display: inline-block;
  }
  .grid-2 {
    display: grid; grid-template-columns: 1fr 1fr; gap: 6px 12px;
  }
  .kv {
    display: flex; align-items: center; justify-content: space-between;
    padding: 6px 10px;
    background: #fff;
    border: 1px solid var(--ink-200);
    border-radius: 8px;
    gap: 8px;
  }
  .kv .k { color: var(--ink-400); font-size: 10.5px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.4px; }
  .kv .v { color: var(--ink-900); font-weight: 700; font-size: 12px; text-align: end; }
  .kv.total { background: linear-gradient(180deg, #fff 0%, #ecfeff 100%); border-color: var(--blue-500); }
  .kv.total .v { color: var(--blue-700); }
  .note-box {
    margin-top: 8px;
    padding: 10px 12px;
    background: #fff;
    border: 1px solid var(--ink-200);
    border-inline-start: 4px solid var(--blue-500);
    border-radius: 8px;
    white-space: pre-wrap;
  }
  .note-box .note-label { font-size: 10px; color: var(--ink-400); font-weight: 700; text-transform: uppercase; margin-bottom: 4px; }
  .note-box.internal { border-inline-start-color: var(--orange-500); background: #fffdf7; }
  .cancel-box .section-title { background: linear-gradient(90deg, #dc2626 0%, #f97316 100%); }

  /* ── Actions table ─────────────────────────────────────────── */
  .tbl {
    width: 100%; border-collapse: collapse; margin-top: 4px;
    font-size: 11px;
  }
  .tbl th, .tbl td {
    padding: 8px 10px;
    text-align: ${isAr ? 'right' : 'left'};
    border-bottom: 1px solid var(--ink-200);
    vertical-align: top;
  }
  .tbl th {
    background: var(--tint);
    color: var(--ink-600);
    font-weight: 700;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .tbl tbody tr:nth-child(even) { background: #fafcff; }
  .mono { font-family: 'JetBrains Mono', 'Menlo', monospace; font-size: 10.5px; }
  .chip {
    display: inline-block;
    margin-inline-start: 6px;
    padding: 1px 8px;
    border-radius: 999px;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.3px;
  }
  .chip.fee { background: #ecfeff; color: var(--blue-700); border: 1px solid #7dd3fc; }
  .chip.src-action { background: #f5f3ff; color: #6d28d9; border: 1px solid #c4b5fd; margin-inline-start: 6px; }
  .chip.src-payment { background: #f0fdf4; color: #166534; border: 1px solid #86efac; margin-inline-start: 6px; }
  .chip.src-original { background: #fff7ed; color: #c2410c; border: 1px solid #fdba74; margin-inline-start: 6px; }
  .receipt-chip {
    display: inline-block;
    padding: 2px 8px;
    margin-inline-start: 4px;
    margin-top: 2px;
    border-radius: 6px;
    background: #ecfeff;
    border: 1px solid #7dd3fc;
    color: var(--blue-700);
    font-size: 9.5px;
    font-weight: 600;
  }
  .attn-line { font-size: 10.5px; color: var(--ink-400); margin-top: 3px; }

  /* ── Dirham price with inline icon (iter-217c-v11) ──────── */
  .dh-price {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    direction: ltr;
    font-family: 'Inter', 'Segoe UI', sans-serif;
    font-weight: 600;
  }
  .dh-icon {
    height: 0.9em;
    width: auto;
    object-fit: contain;
    vertical-align: middle;
  }

  /* ── Attachments list ──────────────────────────────────────── */
  .attn-list { margin: 0; padding-inline-start: 16px; }
  .attn-list li { padding: 4px 0; font-size: 11px; }
  .attn-meta { color: var(--ink-400); font-size: 10px; margin-inline-start: 6px; }

  /* ── Image previews grid (iter-217c-v11) ──────────────────── */
  .images-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    margin-top: 8px;
  }
  .image-card {
    margin: 0;
    padding: 6px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #fafafa;
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .image-card img {
    display: block;
    width: 100%;
    max-height: 340px;
    object-fit: contain;
    border-radius: 4px;
    background: white;
  }
  .image-card figcaption {
    margin-top: 6px;
    font-size: 10.5px;
    color: var(--ink);
    display: flex;
    align-items: center;
    gap: 4px;
    flex-wrap: wrap;
  }
  @media print {
    .images-grid { grid-template-columns: repeat(2, 1fr); }
  }

  /* ── Action cards (iter-218 Feb 2026) ─────────────────────
     Each action gets its own card with header (index + title + meta)
     and inline thumbnails for its attachments + receipts. Users
     asked to see "الإجراء رقم 1 + مرفقاته" grouped together. */
  .actions-list { display: flex; flex-direction: column; gap: 10px; }
  .action-card {
    border: 1px solid var(--ink-200);
    border-radius: 10px;
    background: #ffffff;
    padding: 10px 12px;
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .action-head {
    display: grid;
    grid-template-columns: 26px 1fr auto;
    gap: 10px;
    align-items: start;
    padding-bottom: 8px;
    border-bottom: 1px dashed var(--ink-200);
    margin-bottom: 8px;
  }
  .action-num {
    width: 26px; height: 26px;
    display: inline-flex; align-items: center; justify-content: center;
    background: var(--gradient); color: #fff;
    border-radius: 50%; font-weight: 800; font-size: 12px;
  }
  .action-title {
    font-size: 13px; font-weight: 700; color: var(--ink);
    line-height: 1.5;
    display: flex; align-items: center; gap: 6px; flex-wrap: wrap;
  }
  .action-meta {
    display: flex; flex-direction: column; gap: 3px;
    font-size: 10.5px; color: var(--ink-400); text-align: end;
    line-height: 1.4;
  }
  .action-amt { color: var(--ink); font-weight: 700; font-size: 12px; }
  .action-imgs {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    margin-top: 4px;
  }
  .action-img {
    margin: 0;
    padding: 4px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background: #fafafa;
    break-inside: avoid;
    page-break-inside: avoid;
  }
  .action-img img {
    display: block;
    width: 100%;
    max-height: 180px;
    object-fit: contain;
    border-radius: 3px;
    background: #ffffff;
  }
  .action-img figcaption {
    margin-top: 3px;
    font-size: 9.5px;
    color: var(--ink-400);
    text-align: center;
  }
  .action-docs {
    margin-top: 6px;
    font-size: 10.5px;
    color: var(--ink-400);
  }
  .action-docs strong { color: var(--ink); }
  @media print {
    .action-imgs { grid-template-columns: repeat(3, 1fr); }
  }

  /* ── Stages ────────────────────────────────────────────────── */
  .stages { list-style: none; padding: 0; margin: 0; }
  .stages li {
    display: grid;
    grid-template-columns: 34px 1fr 90px 130px 1fr;
    align-items: center;
    padding: 8px 12px;
    border: 1px solid var(--ink-200);
    border-radius: 8px;
    margin-bottom: 5px;
    background: #fff;
    font-size: 11px;
    gap: 8px;
  }
  .stages li.done   { background: #f0fdf4; border-color: #86efac; }
  .stages li.pending{ background: #fafafa; color: var(--ink-400); }
  .stage-num {
    width: 26px; height: 26px; border-radius: 50%;
    display: inline-flex; align-items: center; justify-content: center;
    background: var(--brand-grad); color: #fff; font-weight: 800; font-size: 11px;
  }
  .stages li.pending .stage-num { background: var(--ink-200); color: var(--ink-400); }
  .stage-label   { font-weight: 700; color: var(--ink-900); }
  .stages li.pending .stage-label { color: var(--ink-400); font-weight: 500; }
  .stage-status  { text-align: ${isAr ? 'right' : 'left'}; font-weight: 700; }
  .stages li.done  .stage-status { color: #16a34a; }
  .stage-date    { color: var(--ink-400); font-family: 'JetBrains Mono', monospace; font-size: 10px; }
  .stage-by      { color: var(--ink-400); font-size: 10px; }

  /* ── Footer (fixed on every page) ──────────────────────────── */
  .doc-footer {
    position: fixed;
    bottom: 6mm;
    inset-inline-start: 14mm;
    inset-inline-end: 14mm;
    text-align: center;
  }
  .footer-bar { height: 4px; background: var(--brand-grad); border-radius: 4px; margin-bottom: 6px; }
  .footer-row {
    display: flex; justify-content: space-around; flex-wrap: wrap;
    gap: 8px;
    font-size: 9.5px;
    color: var(--ink-600);
    font-weight: 600;
  }
  .footer-line {
    font-size: 8.5px;
    color: var(--ink-400);
    margin-top: 4px;
    letter-spacing: 0.3px;
  }

  /* ── Print controls (screen only) ──────────────────────────── */
  .print-bar {
    position: sticky; top: 0; z-index: 10;
    padding: 10px 14px;
    background: var(--ink-900);
    color: #fff;
    display: flex; align-items: center; justify-content: space-between;
    gap: 12px;
    box-shadow: 0 4px 10px rgba(0,0,0,0.08);
  }
  .print-bar .lang-switch a {
    color: #fff; text-decoration: none;
    padding: 4px 10px; border-radius: 6px;
    background: rgba(255,255,255,0.08);
    margin-inline-start: 6px;
    font-size: 12px;
  }
  .print-bar button {
    background: var(--brand-grad);
    color: #fff; font-weight: 800;
    border: none; padding: 8px 18px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 13px;
  }
  @media print {
    .print-bar { display: none; }
    body { background: var(--paper); }
    .doc-wrapper { padding: 0; }
  }

  .doc-wrapper { padding: 14px 20px 40mm 20px; max-width: 210mm; margin: 0 auto; }
</style>
</head>
<body>

  <div class="print-bar">
    <span>${downloadMode ? t('حفظ كـ PDF', 'Save as PDF') : t('معاينة قبل الطباعة', 'Print preview')} — ${escapeHtml(task.request_no_display || task.id)}</span>
    <span class="lang-hint">${t('اللغة الحالية:', 'Current language:')} <b>${t('العربية', 'English')}</b></span>
    <button onclick="window.print()">${downloadMode ? t('💾 حفظ PDF', '💾 Save as PDF') : t('🖨️ طباعة', '🖨️ Print')}</button>
  </div>

  <div class="doc-wrapper">
    ${headerHtml}
    ${statusHeroHtml}
    ${cancelHtml}
    ${targetHtml}
    ${feesHtml}
    ${stagesHtml}
    ${actionsHtml}
    ${imagesGridHtml}
    ${attHtml}
  </div>

  ${footerHtml}

  <script>
    // Auto-open the print dialog after images load (best-effort).
    (function(){
      var img = document.querySelector('.brand-logo');
      var ready = function(){ setTimeout(function(){ window.focus(); window.print(); }, 400); };
      if (!img) return ready();
      if (img.complete) return ready();
      img.addEventListener('load',  ready);
      img.addEventListener('error', ready);
    })();
  <\/script>
</body>
</html>`;
};

/**
 * Public entry point — opens a new tab with the print-ready HTML.
 * `opts.language`     = 'ar' | 'en' (defaults to Arabic).
 * `opts.downloadMode` = true → tab title becomes the requested filename so
 *                       "Save as PDF" from the browser dialog uses that name;
 *                       the top bar reads "Save as PDF" instead of "Print".
 *
 * iter-186 (Feb 2026) — Rewrite: instead of window.open('', ...).document.write
 * (which broke in some browsers and blank-tabbed when noopener/noreferrer
 * were set), we build a Blob and open its object URL. This is more reliable,
 * works with strict popup-blockers, and lets us set noopener/noreferrer
 * safely (we no longer need a handle back to the child window).
 */
export const printAssignedTask = (task, opts = {}) => {
  if (!task) return;
  const urlLang = (typeof window !== 'undefined')
    ? new URLSearchParams(window.location.search).get('lang')
    : null;
  const language = (opts.language || urlLang || 'ar').toLowerCase();
  const isAr = language.startsWith('ar');
  const html = buildHtml(task, isAr, { downloadMode: !!opts.downloadMode });

  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url  = URL.createObjectURL(blob);
  const win  = window.open(url, '_blank');
  if (!win) {
    // Popup blocked — fall back to navigating the current tab.
    window.location.href = url;
    return;
  }
  // Release the object URL a bit later so the child window has time to load.
  setTimeout(() => { URL.revokeObjectURL(url); }, 60_000);
};

/**
 * iter-217c-v15 (Feb 2026) — REAL PDF download (not print dialog).
 *
 * User complained the previous "downloadMode" still just opened a print
 * dialog. This version uses `html2pdf.js` (jsPDF + html2canvas) so the
 * browser SAVES a real .pdf file directly.
 *
 * Fix for the "blank white PDF" bug that was observed on production:
 *   • Wait for `document.fonts.ready` so Cairo/Inter are actually rendered
 *     when html2canvas takes the DOM snapshot.
 *   • Preload every `<img>` inside the container (wait for `load` OR
 *     `error` events) — html2canvas silently ignores in-flight images and
 *     was producing a page-with-no-images before.
 *   • Position the render container in-viewport but invisible (opacity 0)
 *     instead of at `left: -99999px` — Chrome sometimes refuses to lay out
 *     off-screen fixed nodes, which was the root cause of the totally
 *     blank PDF.
 *
 * Filename: `بيانات_المقدم_{target_name}_طلب_{request_no}.pdf` — matches
 * the user spec (no agent name, no `-ar/-en` suffix).
 */
export const downloadAssignedTaskPDF = async (task, opts = {}) => {
  if (!task) return;
  const language = (opts.language || 'ar').toLowerCase();
  const isAr = language.startsWith('ar');

  // Build the same HTML we use for print, but STRIP the print bar & scripts
  // since we're rendering a static snapshot.
  const html = buildHtml(task, isAr, { downloadMode: true });

  // iter-218 (Feb 2026) — Full-viewport locking wrapper to stop the
  // viewport from being resized by our off-screen container's width.
  // Previously the page appeared to "shrink then grow" because the
  // 210mm A4 container temporarily forced horizontal scroll (esp. on
  // mobile), which some browsers respond to by zooming the viewport.
  // We add a fixed transparent shield that locks scroll for the entire
  // render, then remove it when done.
  const shield = document.createElement('div');
  shield.setAttribute('data-pdf-shield', '1');
  shield.style.cssText = [
    'position:fixed',
    'inset:0',
    'z-index:2147483000',
    'background:rgba(0,0,0,0.55)',
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'color:#fff',
    'font-family:sans-serif',
    'font-size:14px',
    'letter-spacing:0.3px',
  ].join(';');
  shield.innerHTML = `<div style="text-align:center">
    <div style="width:40px;height:40px;border:3px solid rgba(255,255,255,0.25);border-top-color:#fff;border-radius:50%;margin:0 auto 12px;animation:pdfSpin 0.9s linear infinite"></div>
    <div>${isAr ? 'جارٍ إنشاء ملف PDF…' : 'Generating PDF…'}</div>
  </div>
  <style>@keyframes pdfSpin{to{transform:rotate(360deg)}}</style>`;
  // Prevent the page's normal scroll from moving while we render.
  const prevBodyOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';
  document.body.appendChild(shield);

  const container = document.createElement('div');
  // Off-screen but IN-DOM and FULLY LAID OUT. Using `visibility:hidden` or
  // `opacity:0` makes html2canvas render an empty page in some browsers,
  // so we push it far off-screen instead and keep it fully visible.
  // iter-218 — Also set `contain: layout style paint` so the container's
  // 210mm width does NOT push the document scrollbar / trigger a viewport
  // reflow that mobile browsers respond to with a zoom.
  container.style.position = 'fixed';
  container.style.left = '-20000px';
  container.style.top  = '0';
  container.style.width = '210mm';           // A4 width
  container.style.background = '#ffffff';
  container.style.color = '#0f172a';
  container.style.fontFamily = isAr ? 'Cairo, Arial, sans-serif' : 'Inter, Arial, sans-serif';
  container.style.fontSize = '12px';
  container.style.lineHeight = '1.55';
  container.style.zIndex = '-1';
  container.style.contain = 'layout style paint';
  container.style.pointerEvents = 'none';
  container.innerHTML = html;
  container.querySelectorAll('.print-bar, script, title').forEach((el) => el.remove());
  document.body.appendChild(container);

  // Force layout so the browser measures real content height before html2canvas.
  void container.offsetHeight;

  // Move all `<style>` blocks from inside `container` to `<head>` so their
  // rules ALSO apply to the cloned iframe html2canvas builds internally.
  // Without this the tag selectors (`html`, `body`, `.doc-wrapper`, `.section`
  // …) fail to cascade because the container itself is a plain <div>, not
  // <body>, so `body { font-family:… }` doesn't hit anything.
  const injectedStyles = [];
  container.querySelectorAll('style').forEach((s) => {
    const clone = s.cloneNode(true);
    clone.setAttribute('data-pdf-injected', 'true');
    document.head.appendChild(clone);
    injectedStyles.push(clone);
  });

  // Render TARGET is the .doc-wrapper (the actual content), NOT the container
  // itself — the container also holds <meta>, <style>, <title> which have
  // zero visual weight and produced an empty 3KB PDF before this fix.
  const renderTarget = container.querySelector('.doc-wrapper') || container;

  try {
    // ── 1) Wait for web-fonts (Cairo/Inter loaded via @import). Without this,
    //       html2canvas snapshots with fallback fonts → mis-aligned text.
    if (document.fonts && typeof document.fonts.ready?.then === 'function') {
      await document.fonts.ready;
    }
    // ── 2) Preload EVERY image (logo + dirham icons + attachments/receipts).
    //       This is the primary fix for the "blank PDF" bug — html2canvas
    //       otherwise snapshots before base64 attachments finish decoding.
    const imgs = Array.from(container.querySelectorAll('img'));
    await Promise.all(imgs.map((img) => {
      if (img.complete && img.naturalWidth > 0) return Promise.resolve();
      return new Promise((resolve) => {
        const done = () => resolve();
        img.addEventListener('load',  done, { once: true });
        img.addEventListener('error', done, { once: true });
        // Safety timeout — never block PDF generation for > 4s on a slow img.
        setTimeout(done, 4000);
      });
    }));

    // ── 3) Lazy-load html2pdf and render.
    const html2pdfModule = await import('html2pdf.js');
    const html2pdf = html2pdfModule.default || html2pdfModule;

    const filename = `${buildFilenameBase(task)}.pdf`;
    await html2pdf()
      .from(renderTarget)
      .set({
        margin:       [8, 8, 12, 8],           // mm — top, right, bottom, left
        filename,
        image:        { type: 'jpeg', quality: 0.92 },
        html2canvas:  {
          scale:            2,
          useCORS:          true,
          allowTaint:       true,
          letterRendering:  true,
          backgroundColor:  '#ffffff',
          logging:          false,
        },
        jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait', compress: true },
        pagebreak:    { mode: ['css', 'legacy', 'avoid-all'] },
      })
      .save();
  } finally {
    if (container.parentNode) container.parentNode.removeChild(container);
    injectedStyles.forEach((s) => s.parentNode && s.parentNode.removeChild(s));
    // iter-218 — remove the loading shield + restore body overflow.
    if (shield.parentNode) shield.parentNode.removeChild(shield);
    document.body.style.overflow = prevBodyOverflow;
  }
};
